import React, { useState, useContext } from 'react';
import * as PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { storeContext } from '../../../../../context';
import { t } from '../../../../../../utils/i18next';
import PaymentButtonWrapper from '../../../../general/PaymentButtonWrapper';
import OneClickPaymentDialog from '../../../../general/PaymentConfirmation/OneClickPaymentDialog';
import masterCardLogo from '../../../../general/paymentInformation/assets/creditcard/masterCardLogo.png';
import { dateInUTC } from '../../../../../../utils/Date';
import {
  eventNames,
  paymentInfoPropertyNames,
  paymentStartPropertyNames,
  paymentSelectionPropertyNames,
  paymentTypes
} from '../../../../../../constants/mixpanel';

const { ONE_CLICK_PAYMENT } = paymentTypes;
const { PAYMENT_SUCCESS, PAYMENT_FAILURE } = eventNames;
const { PAYMENT_TYPE } = paymentInfoPropertyNames;
const translationBase = 'payables.total_side_bar.payment';

const OneClickPaymentButton = observer(
  ({ balance, amountToPay, creditCard }) => {
    const {
      router: { history },
      payable: {
        appointmentsPaymentParams,
        discount,
        fetchFeePayment,
        getAutoPaymentOption,
        selectedBillsIds,
        selectedBillIdsInsideThePlan
      },
      paymentModel: { validateCreditCard, performCreditCardPayment },
      reportError,
      settings: { get },
      translations: { currentLanguage },
      tracking,
      userState
    } = useContext(storeContext);

    const [showError, setShowError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPaymentConfirmationPopup, setShowPaymentConfirmationPopup] =
      useState(false);

    const paymentDetails = {
      amount: (amountToPay / 100).toFixed(2),
      date: dateInUTC(),
      balance: (balance / 100).toFixed(2),
      cardEnding: creditCard.last4
    };

    const submitPayment = async () => {
      setIsSubmitting(true);
      let confirmationCode = null;

      try {
        const feeAmount = await fetchFeePayment(amountToPay);
        const ccData = { id: creditCard.id };

        await validateCreditCard(ccData);
        confirmationCode = await processPayment(feeAmount);
        updatePaymentInformation(confirmationCode);
        navigateToReceipt();
      } catch (error) {
        console.error(error);
        setShowError(true);
        reportError({ errorMessage: error, query: 'creditCardPayment' });
      } finally {
        setIsSubmitting(false);
        sendTracking(confirmationCode);
      }
    };

    const processPayment = async feeAmount => {
      return await performCreditCardPayment(
        amountToPay,
        paymentDetails.date,
        {},
        selectedBillsIds,
        feeAmount,
        !!get('scheduled_payment'),
        discount,
        false,
        appointmentsPaymentParams(
          paymentDetails.balance,
          paymentDetails.amount
        ),
        selectedBillIdsInsideThePlan
      );
    };

    const updatePaymentInformation = confirmationCode => {
      userState.set('paymentInformation', {
        ...getAutoPaymentOption(ONE_CLICK_PAYMENT),
        confirmationCode
      });
    };

    const navigateToReceipt = () => {
      history.push('/payment');
    };

    const sendTracking = confirmationCode => {
      const paymentResult = confirmationCode
        ? PAYMENT_SUCCESS
        : PAYMENT_FAILURE;
      tracking.track(paymentResult, {
        ...tracking.getExistingEntries([
          ...Object.values(paymentStartPropertyNames),
          ...Object.values(paymentSelectionPropertyNames)
        ]),
        [PAYMENT_TYPE]: ONE_CLICK_PAYMENT
      });
    };

    return (
      <>
        <OneClickPaymentDialog
          open={showPaymentConfirmationPopup}
          paymentDetails={paymentDetails}
          isSubmitting={isSubmitting}
          showError={showError}
          handleClose={setShowPaymentConfirmationPopup}
          submitPayment={submitPayment}
        />
        <PaymentButtonWrapper
          variant="contained"
          color="primary"
          size="large"
          aria-label={ONE_CLICK_PAYMENT}
          paymentButtonType={ONE_CLICK_PAYMENT}
          disabled={balance <= 0 || amountToPay <= 0}
          onClick={() => setShowPaymentConfirmationPopup(true)}
        >
          {t(`${translationBase}.one_click_payment`)} - {creditCard.last4}
          <img src={masterCardLogo} alt={'MasterCard Logo'} />
        </PaymentButtonWrapper>
      </>
    );
  }
);

OneClickPaymentButton.propTypes = {
  balance: PropTypes.number.isRequired,
  amountToPay: PropTypes.number.isRequired,
  creditCard: PropTypes.object.isRequired
};

export default OneClickPaymentButton;
